import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  HomeIcon, 
  UsersIcon, 
  KeyIcon, 
  TagIcon,
  ComputerDesktopIcon, 
  ChartBarIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { ROUTES } from '../../constants';

const navigation = [
  { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: HomeIcon },
  { name: 'Clientes', href: ROUTES.CLIENTS, icon: UsersIcon },
  { name: 'Licencias - Contratos', href: ROUTES.LICENSES, icon: KeyIcon },
  { name: 'Tipos de Licencia', href: ROUTES.LICENSE_TYPES, icon: TagIcon },
  { name: 'Software', href: ROUTES.SOFTWARE, icon: ComputerDesktopIcon },
  { name: 'Reportes', href: ROUTES.REPORTS, icon: ChartBarIcon },
  { name: 'Configuración', href: ROUTES.SETTINGS, icon: Cog6ToothIcon },
];

const adminNavigation = [
  { name: 'Administración', href: '/admin/', icon: ShieldCheckIcon, external: true },
];

export const Sidebar = () => {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { user } = useAuthStore();

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={toggleSidebar}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out',
        sidebarOpen ? 'w-64 translate-x-0' : 'w-16 translate-x-0'
      )}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            {sidebarOpen && (
              <div className="flex items-center">
                <KeyIcon className="h-8 w-8 text-primary-600" />
                <div className="ml-2">
                  <div className="text-lg font-bold text-gray-900 leading-tight">
                    DICOMITY
                  </div>
                  <div className="text-sm font-semibold text-gray-600 leading-tight">
                    License Manager
                  </div>
                </div>
              </div>
            )}
            
            {!sidebarOpen && (
              <KeyIcon className="h-8 w-8 text-primary-600 mx-auto" />
            )}
            
            <button
              onClick={toggleSidebar}
              className="lg:hidden rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              {sidebarOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                    !sidebarOpen && 'justify-center'
                  )
                }
                title={!sidebarOpen ? item.name : undefined}
              >
                <item.icon
                  className={cn(
                    'h-6 w-6 flex-shrink-0',
                    !sidebarOpen ? 'mx-auto' : 'mr-3'
                  )}
                />
                {sidebarOpen && <span>{item.name}</span>}
              </NavLink>
            ))}
            
            {/* Admin Navigation - Only for superusers */}
            {user?.is_superuser && (
              <>
                <div className="border-t border-gray-200 my-2"></div>
                {adminNavigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                      'text-blue-600 hover:bg-blue-50 hover:text-blue-700',
                      !sidebarOpen && 'justify-center'
                    )}
                    title={!sidebarOpen ? item.name : undefined}
                  >
                    <item.icon
                      className={cn(
                        'h-6 w-6 flex-shrink-0',
                        !sidebarOpen ? 'mx-auto' : 'mr-3'
                      )}
                    />
                    {sidebarOpen && <span>{item.name}</span>}
                  </a>
                ))}
              </>
            )}
          </nav>

          {/* User info */}
          {sidebarOpen && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">
                    {user?.names?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center">
                    <p className="text-sm font-medium text-gray-700">
                      {user?.names} {user?.last_name}
                    </p>
                    {user?.is_superuser && (
                      <ShieldCheckIcon className="h-4 w-4 text-blue-600 ml-2" title="Superusuario" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {user?.is_superuser ? 'Superusuario' : user?.is_staff ? 'Staff' : 'Usuario'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

